import { PageHeading } from "@/components/ui/page";
import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Save, RefreshCw, ExternalLink, Copy, Eye } from "lucide-react";
import { posAPI, branchesAPI, usersAPI, productsAPI, companyAPI } from "@/lib/api";
import { useCompanyProfile } from "@/hooks/useCompanyProfile";
import { Switch } from "@/components/ui/switch";
import { Pos, PosStatus, Branch, User, Product } from "@/types";
import { useTranslation } from "react-i18next";
import { useToast } from "@/hooks/use-toast";
import { Loading } from "@/components/ui/loader";
import { Checkbox } from "@/components/ui/checkbox";
import { TablesManager } from "@/features/pos/components/TablesManager";
import { OrderTypesManager } from "@/features/pos/components/OrderTypesManager";
import { OrderChannelsManager } from "@/features/pos/components/OrderChannelsManager";
import { PaymentMethodBranchToggles } from "@/features/pos/components/PaymentMethodBranchToggles";
import { PrinterConfigManager } from "@/features/pos/components/PrinterConfigManager";
import { StorefrontPreview } from "@/features/pos/components/StorefrontPreview";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { StorefrontSettingsFields } from "@/features/pos/components/StorefrontSettings";
import { useStorefrontSettings } from "@/features/pos/hooks/useStorefrontSettings";
import { usePermission } from "@/hooks/usePermission";
import { StorefrontBannersSettings } from "@/features/pos/components/StorefrontBannersSettings";
import {
	hasValidStorefrontBanners,
	isStorefrontBanner,
	type StorefrontBanner,
} from "@/features/pos/storefront-banners";
import { SectionHead } from "@/components/ui/section-head";
import { cn } from "@/lib/utils";
import { useIsDirty, useUnsavedChangesGuard } from "@/hooks/useUnsavedChangesGuard";

const MENU_PUBLIC_BASE = "https://store.cashiero.net";

const PosDetailPage: React.FC = () => {
	const navigate = useNavigate();
	const { id } = useParams<{ id: string }>();
	const { t } = useTranslation();
	const { toast } = useToast();

	const isEditing = !!id;
	const [loading, setLoading] = useState(isEditing);
	const [saving, setSaving] = useState(false);
	const [previewOpen, setPreviewOpen] = useState(false);
	const [createdPosId, setCreatedPosId] = useState<number | null>(null);

	const [pos, setPos] = useState<Partial<Pos>>({
		name: "",
		code: "",
		status: PosStatus.ACTIVE,
		serialNumber: "",
		notes: "",
		branchId: 0,
		isMenuWeb: false,
		offersTableService: false,
	});
	const { data: companyProfile } = useCompanyProfile();
	const [tenantCompanyId, setTenantCompanyId] = useState<number | null>(null);
	const [menuSlugEdit, setMenuSlugEdit] = useState("");
	const [storefrontBanners, setStorefrontBanners] = useState<StorefrontBanner[]>([]);

	const canUpdateStorefront = usePermission("storefront.update");
	const canReadCompany = usePermission("company.read");
	const storefrontEnabled = !!pos.isMenuWeb && canUpdateStorefront && canReadCompany;
	const storefrontSettings = useStorefrontSettings(storefrontEnabled, id);
	useEffect(() => {
		if (!isEditing && storefrontSettings.profile) {
			const stored = storefrontSettings.profile.settings?.storefrontBanners;
			setStorefrontBanners(Array.isArray(stored) ? stored.filter(isStorefrontBanner) : []);
		}
	}, [isEditing, storefrontSettings.profile]);

	const [branches, setBranches] = useState<Branch[]>([]);
	const [employees, setEmployees] = useState<User[]>([]);
	const [products, setProducts] = useState<Product[]>([]);
	const [selectedEmployeeIds, setSelectedEmployeeIds] = useState<number[]>([]);
	const [selectedProductIds, setSelectedProductIds] = useState<number[]>([]);
	const { isDirty, markSaved } = useIsDirty(
		{ pos, menuSlugEdit, storefrontBanners, selectedEmployeeIds, selectedProductIds },
		isEditing ? !loading && Boolean(pos.id) : true,
	);
	const { allowNavigation } = useUnsavedChangesGuard(
		(isDirty || storefrontSettings.dirty || !!storefrontSettings.pendingUploads) && !saving,
	);
	const [productSearchTerm, setProductSearchTerm] = useState("");
	const previousBranchIdRef = useRef<number | null | undefined>(undefined);

	const fetchPos = useCallback(async () => {
		if (!id) return;
		try {
			setLoading(true);
			const data = await posAPI.getById(id);
			setPos(data);
			setMenuSlugEdit((data.menuSlug as string) || "");
			setSelectedEmployeeIds(data.employees?.map((emp: User) => emp.id) || []);
			setSelectedProductIds(data.products?.map((prod: Product) => prod.id) || []);
			if (data.isMenuWeb) {
				const config = await posAPI.getStorefrontBanners(id);
				setStorefrontBanners(Array.isArray(config?.banners) ? config.banners.filter(isStorefrontBanner) : []);
			} else {
				setStorefrontBanners([]);
			}
		} catch (error) {
			toast({
				title: t("common.error"),
				description: t("pos.errors.fetchFailed"),
				variant: "destructive",
			});
			navigate("/pos/list");
		} finally {
			setLoading(false);
		}
	}, [id, toast, t, navigate]);

	useEffect(() => {
		fetchBranches();
		if (isEditing && id) {
			fetchPos();
		}
	}, [id, isEditing, fetchPos]);

	useEffect(() => {
		if (!companyProfile) {
			setTenantCompanyId(null);
			return;
		}
		const raw = companyProfile.id;
		const cid = typeof raw === "string" ? parseInt(raw, 10) : raw;
		setTenantCompanyId(Number.isFinite(Number(cid)) ? Number(cid) : null);
	}, [companyProfile]);

	useEffect(() => {
		const isMenu = !!pos.isMenuWeb;
		const branchId = pos.branchId != null && Number(pos.branchId) > 0 ? Number(pos.branchId) : null;
		const prev = previousBranchIdRef.current;
		if (!isMenu && prev != null && branchId != null && prev !== branchId) {
			setSelectedEmployeeIds([]);
			setSelectedProductIds([]);
		}
		previousBranchIdRef.current = branchId;

		if (isMenu) {
			fetchProductsCompanyWide();
			fetchEmployeesCompanyWide();
			return;
		}
		if (branchId) {
			fetchEmployees(branchId);
			fetchProducts(branchId);
		} else {
			setEmployees([]);
			setProducts([]);
		}
	}, [pos.branchId, pos.isMenuWeb]);

	const fetchBranches = async () => {
		try {
			const data = await branchesAPI.getAll();
			setBranches(data || []);
		} catch (error) {
			console.error("Failed to fetch branches:", error);
		}
	};

	const fetchEmployees = async (branchId: number) => {
		try {
			const data = await usersAPI.getAll({ branchId, limit: 500 });
			setEmployees(data?.data ?? data ?? []);
		} catch (error) {
			console.error("Failed to fetch employees:", error);
			setEmployees([]);
		}
	};

	const fetchProducts = async (branchId: number) => {
		try {
			const data = await productsAPI.getAll({ branchId, limit: 1000, page: 1 });
			setProducts(data?.products ?? []);
		} catch (error) {
			console.error("Failed to fetch products:", error);
			setProducts([]);
		}
	};

	const fetchProductsCompanyWide = async () => {
		try {
			const data = await productsAPI.getAll({ limit: 3000, page: 1 });
			setProducts(data?.products ?? []);
		} catch (error) {
			console.error("Failed to fetch products:", error);
			setProducts([]);
		}
	};

	const fetchEmployeesCompanyWide = async () => {
		try {
			const data = await usersAPI.getAll({ limit: 500 });
			setEmployees(data?.data ?? data ?? []);
		} catch (error) {
			console.error("Failed to fetch employees:", error);
			setEmployees([]);
		}
	};

	const handleInputChange = (field: keyof Pos, value: any) => {
		setPos(prev => ({ ...prev, [field]: value }));
	};

	const handleGenerateCode = async () => {
		try {
			const response = await posAPI.generateCode();
			setPos(prev => ({ ...prev, code: response.code }));
			toast({
				title: t("common.success"),
				description: t("pos.messages.codeGenerated"),
			});
		} catch (error) {
			toast({
				title: t("common.error"),
				description: t("pos.errors.codeGenerationFailed"),
				variant: "destructive",
			});
		}
	};

	const handleEmployeeToggle = (employeeId: number) => {
		setSelectedEmployeeIds(prev =>
			prev.includes(employeeId) ? prev.filter(id => id !== employeeId) : [...prev, employeeId],
		);
	};

	const handleProductToggle = (productId: number) => {
		setSelectedProductIds(prev =>
			prev.includes(productId) ? prev.filter(id => id !== productId) : [...prev, productId],
		);
	};

	const handleSelectAllProducts = (checked: boolean) => {
		if (checked) {
			setSelectedProductIds(products.map(p => p.id));
		} else {
			setSelectedProductIds([]);
		}
	};

	const allProductsSelected = products.length > 0 && selectedProductIds.length === products.length;
	const someProductsSelected = selectedProductIds.length > 0 && selectedProductIds.length < products.length;

	const handleSubmit = async () => {
		const isMenu = !!pos.isMenuWeb;
		if (storefrontSettings.pendingUploads) return;
		const name =
			pos.name?.trim() ||
			(isMenu ? storefrontSettings.draft.appearance.displayName.trim() || companyProfile?.name : "");
		if (!name) {
			toast({
				title: t("common.error"),
				description: t("pos.errors.requiredFields"),
				variant: "destructive",
			});
			return;
		}
		if (!isMenu && (!pos.branchId || Number(pos.branchId) <= 0)) {
			toast({
				title: t("common.error"),
				description: t("pos.errors.branchRequiredStandard"),
				variant: "destructive",
			});
			return;
		}
		if (isMenu && tenantCompanyId == null) {
			toast({
				title: t("common.error"),
				description: t("pos.errors.tenantCompanyRequired"),
				variant: "destructive",
			});
			return;
		}
		if (isMenu && !hasValidStorefrontBanners(storefrontBanners)) {
			toast({
				title: t("common.error"),
				description: t("company.settings.storefront.banners.invalidConfiguration"),
				variant: "destructive",
			});
			return;
		}

		try {
			setSaving(true);
			const data: Record<string, unknown> = {
				name,
				code: pos.code,
				status: pos.status,
				serialNumber: pos.serialNumber,
				notes: pos.notes,
				employeeIds: selectedEmployeeIds,
				productIds: selectedProductIds,
				isMenuWeb: isMenu,
				offersTableService: isMenu ? false : !!pos.offersTableService,
			};
			if (isMenu) {
				data.companyId = tenantCompanyId;
				data.branchId = null;
				if ((isEditing || createdPosId) && menuSlugEdit.trim()) {
					data.menuSlug = menuSlugEdit.trim().toLowerCase();
				}
			} else {
				data.branchId = pos.branchId;
			}

			let targetId: number | string | null = id || createdPosId;
			if (targetId) await posAPI.update(targetId, data);
			else {
				const created = await posAPI.create(data);
				targetId = created.id;
				setCreatedPosId(created.id);
				if (created.menuSlug) setMenuSlugEdit(created.menuSlug);
			}
			if (isMenu && targetId) {
				if (isEditing) await posAPI.updateStorefrontBanners(targetId, storefrontBanners);
				else if (storefrontEnabled) await companyAPI.updateStorefrontContent({ banners: storefrontBanners });
				if (storefrontEnabled && storefrontSettings.dirty) await storefrontSettings.save();
			}
			markSaved();
			toast({
				title: t("common.success"),
				description: t(isEditing ? "pos.messages.updated" : "pos.messages.created"),
			});
			if (!isEditing) {
				allowNavigation();
				navigate(`/pos/${targetId}`);
			}
		} catch (error: any) {
			const msg = error?.response?.data?.message;
			toast({
				title: t("common.error"),
				description: msg ? t(msg) : t("pos.errors.createFailed"),
				variant: "destructive",
			});
		} finally {
			setSaving(false);
		}
	};

	const filteredProducts = products.filter(product =>
		productSearchTerm
			? product.name.toLowerCase().includes(productSearchTerm.toLowerCase()) ||
				product.barcode?.toLowerCase().includes(productSearchTerm.toLowerCase()) ||
				product.sku?.toLowerCase().includes(productSearchTerm.toLowerCase())
			: true,
	);

	if (loading) return <Loading />;

	return (
		<div className="container mx-auto max-w-full space-y-4 px-3 py-4 md:px-4 md:py-5">
			<div className="flex items-center justify-between">
				<div className="flex items-center gap-4">
					<div>
						<PageHeading className="mb-2 ">
							{pos.isMenuWeb
								? t(isEditing ? "storefrontDesign.editTitle" : "storefrontDesign.addTitle")
								: isEditing
									? t("pos.actions.edit")
									: t("pos.actions.create")}
						</PageHeading>
					</div>
				</div>
			</div>

			<div className="rounded-2xl border bg-card p-4">
				{" "}
				<div className="flex items-center justify-between gap-4">
					<div className="space-y-1">
						<Label htmlFor="isMenuWeb" className="text-base">
							{t("pos.form.menuWeb")}
						</Label>
						<p className="text-sm text-muted-foreground">{t("pos.form.menuWebHint")}</p>
					</div>
					<Switch
						id="isMenuWeb"
						checked={!!pos.isMenuWeb}
						onCheckedChange={checked => {
							handleInputChange("isMenuWeb", checked);
							if (checked) {
								if (!pos.name) handleInputChange("name", companyProfile?.name || "");
								handleInputChange("branchId", null);
							} else {
								handleInputChange("branchId", branches[0]?.id ?? 0);
							}
						}}
					/>
				</div>
			</div>
			{pos.isMenuWeb && !storefrontEnabled && (
				<p role="status" className="text-sm text-muted-foreground">
					{t("storefrontDesign.restricted")}
				</p>
			)}
			{createdPosId && !isEditing && (
				<p role="status" className="rounded-xl bg-warning-soft p-3 text-sm text-warning">
					{t("storefrontDesign.createdRetry")}
				</p>
			)}
			{storefrontEnabled && (
				<div className="xl:hidden">
					<Button variant="outline" className="xl:hidden" onClick={() => setPreviewOpen(true)}>
						<Eye className="h-4 w-4" aria-hidden />
						{t("storefrontDesign.preview")}
					</Button>
				</div>
			)}
			<div className={cn("grid items-start gap-5", storefrontEnabled && "xl:grid-cols-[minmax(0,1fr)_340px]")}>
				<div className="min-w-0 space-y-4">
					{storefrontEnabled && (
						<section id="online-storefront" className="space-y-4">
							<StorefrontSettingsFields
								model={storefrontSettings}
								editing={!saving}
								showPreview={false}
							/>
							<div id="storefront-banners">
								<StorefrontBannersSettings
									banners={storefrontBanners}
									onChange={setStorefrontBanners}
									onUploadStart={storefrontSettings.beginUpload}
									onUploadEnd={storefrontSettings.endUpload}
									editing={!saving}
								/>
							</div>
						</section>
					)}
					{!storefrontEnabled && isEditing && pos.isMenuWeb && (
						<StorefrontBannersSettings
							banners={storefrontBanners}
							onChange={setStorefrontBanners}
							onUploadStart={storefrontSettings.beginUpload}
							onUploadEnd={storefrontSettings.endUpload}
							editing={!saving}
						/>
					)}
					<div id="storefront-general" />

					<SectionHead num={1} title={t("pos.tabs.basicData")} hint={t("pos.form.basicDataDescription")} />
					<Card>
						<CardContent className="space-y-6 pt-6">
							<div className="grid grid-cols-1 gap-3 md:grid-cols-2">
								<div className="space-y-2">
									<Label htmlFor="name">{t("pos.form.name")} *</Label>
									<Input
										id="name"
										value={pos.name ?? ""}
										onChange={e => handleInputChange("name", e.target.value)}
										placeholder={t("pos.form.namePlaceholder")}
									/>
								</div>

								<div className="space-y-2">
									<Label htmlFor="code">{t("pos.form.code")}</Label>
									<div className="flex gap-2">
										<Input
											id="code"
											value={pos.code ?? ""}
											onChange={e => handleInputChange("code", e.target.value)}
											placeholder={t("pos.form.codePlaceholder")}
										/>
										<Button
											type="button"
											variant="outline"
											size="sm"
											className="h-10 w-10"
											onClick={handleGenerateCode}
											title={t("common.generate")}
											aria-label={t("common.generate")}
										>
											<RefreshCw className="h-4 w-4" />
										</Button>
									</div>
								</div>
							</div>

							<div className="grid grid-cols-1 gap-3 md:grid-cols-2">
								<div className="space-y-2">
									<Label htmlFor="status">{t("pos.form.status")}</Label>
									<Select
										value={pos.status}
										onValueChange={value => handleInputChange("status", value as PosStatus)}
									>
										<SelectTrigger aria-label={t("pos.form.status")}>
											<SelectValue />
										</SelectTrigger>
										<SelectContent>
											<SelectItem value={PosStatus.ACTIVE}>{t("common.active")}</SelectItem>
											<SelectItem value={PosStatus.INACTIVE}>{t("common.inactive")}</SelectItem>
										</SelectContent>
									</Select>
								</div>

								<div className="space-y-2">
									<Label htmlFor="serialNumber">{t("pos.form.serialNumber")}</Label>
									<Input
										id="serialNumber"
										value={pos.serialNumber ?? ""}
										onChange={e => handleInputChange("serialNumber", e.target.value)}
										placeholder={t("pos.form.serialNumberPlaceholder")}
									/>
								</div>
							</div>

							<div className="space-y-2">
								<Label htmlFor="notes">{t("pos.form.notes")}</Label>
								<Input
									id="notes"
									type="text"
									value={pos.notes ?? ""}
									onChange={e => handleInputChange("notes", e.target.value)}
									placeholder={t("pos.form.notesPlaceholder")}
								/>
							</div>
						</CardContent>
					</Card>

					<SectionHead
						num={2}
						title={t("pos.tabs.assignments")}
						hint={t("pos.form.assignmentsDescription")}
					/>
					<Card>
						<CardContent className="space-y-6 pt-6">
							<div className="space-y-2">
								<Label htmlFor="branchId">
									{t("pos.form.branch")}
									{!pos.isMenuWeb ? " *" : ""}
								</Label>
								{pos.isMenuWeb ? (
									<p className="text-sm text-muted-foreground border rounded-lg p-4">
										{t("pos.form.menuWebBranchNote")}
									</p>
								) : (
									<Select
										value={pos.branchId != null && pos.branchId !== 0 ? String(pos.branchId) : ""}
										onValueChange={value =>
											handleInputChange("branchId", value ? Number(value) : 0)
										}
									>
										<SelectTrigger aria-label={t("pos.form.branch")}>
											<SelectValue placeholder={t("pos.form.selectBranch")} />
										</SelectTrigger>
										<SelectContent>
											{branches.map(branch => (
												<SelectItem key={branch.id} value={String(branch.id)}>
													{branch.name}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
								)}
							</div>

							<div className="space-y-4">
								<Label>{t("pos.form.employees")}</Label>
								{!pos.isMenuWeb && (!pos.branchId || Number(pos.branchId) <= 0) ? (
									<p className="text-muted-foreground text-sm border rounded-lg p-4">
										{t("pos.form.selectBranchFirst")}
									</p>
								) : (
									<div className="flex flex-wrap gap-2 rounded-lg border p-4">
										{employees.map(employee => {
											const on = selectedEmployeeIds.includes(employee.id);
											return (
												<button
													key={employee.id}
													type="button"
													onClick={() => handleEmployeeToggle(employee.id)}
													title={employee.email}
													className={cn(
														"flex items-center gap-2 rounded-full border px-4 py-2 text-sm transition-colors",
														on
															? "border-primary bg-secondary font-semibold text-primary"
															: "bg-card text-muted-foreground hover:border-primary/50",
													)}
												>
													<span
														className={cn(
															"h-2 w-2 rounded-full",
															on ? "bg-primary" : "border border-muted-foreground/40",
														)}
													/>
													{employee.firstName} {employee.lastName}
												</button>
											);
										})}
									</div>
								)}
							</div>
						</CardContent>
					</Card>

					<SectionHead num={3} title={t("pos.tabs.menu")} hint={t("pos.form.menuDescription")} />
					<Card>
						<CardContent className="space-y-4 pt-6">
							<div className="rounded-lg border p-4 space-y-4 bg-muted/30">
								{pos.isMenuWeb && (
									<div className="space-y-3 pt-2 border-t">
										{isEditing && (
											<div className="space-y-2">
												<Label htmlFor="menuSlug">{t("pos.form.menuSlug")}</Label>
												<Input
													id="menuSlug"
													value={menuSlugEdit}
													onChange={e => setMenuSlugEdit(e.target.value)}
													placeholder={t("pos.form.menuSlugPlaceholder")}
													dir="ltr"
													className="font-mono text-sm"
												/>
												<p className="text-xs text-muted-foreground">
													{t("pos.form.menuSlugHint")}
												</p>
											</div>
										)}
										{(menuSlugEdit || pos.menuSlug) && (
											<div className="flex flex-wrap items-center gap-2">
												<ExternalLink className="h-4 w-4 text-muted-foreground shrink-0" />
												<code className="text-sm bg-background px-2 py-1 rounded border break-all">
													{`${MENU_PUBLIC_BASE.replace(/\/$/, "")}/${(menuSlugEdit || pos.menuSlug || "").replace(/^\//, "")}`}
												</code>
												<Button
													type="button"
													variant="outline"
													size="sm"
													className="shrink-0"
													onClick={() => {
														const slug = (menuSlugEdit || pos.menuSlug || "").trim();
														if (!slug) return;
														const url = `${MENU_PUBLIC_BASE.replace(/\/$/, "")}/${slug}`;
														void navigator.clipboard.writeText(url);
														toast({
															title: t("common.success"),
															description: t("pos.messages.menuUrlCopied"),
														});
													}}
												>
													<Copy className="h-4 w-4 me-1" />
													{t("pos.form.copyMenuUrl")}
												</Button>
											</div>
										)}
										{!isEditing && (
											<p className="text-sm text-muted-foreground">
												{t("pos.form.menuSlugAfterCreate")}
											</p>
										)}
									</div>
								)}
							</div>

							{!pos.isMenuWeb && (
								<div className="flex flex-row gap-3 items-center justify-between rounded-lg border p-4 space-y-4 bg-muted/30">
									<div className="space-y-1">
										<Label className="text-base">{t("pos.form.tableService")}</Label>
										<p className="text-sm text-muted-foreground">
											{t("pos.form.tableServiceHint")}
										</p>
									</div>
									<Switch
										aria-label={t("pos.form.tableService")}
										checked={!!pos.offersTableService}
										onCheckedChange={v => handleInputChange("offersTableService", v)}
									/>
								</div>
							)}

							{!pos.isMenuWeb && pos.offersTableService && (
								<div className="space-y-3 rounded-lg border p-4">
									<div className="space-y-1">
										<Label className="text-base">{t("tablesAdmin.title")}</Label>
										<p className="text-sm text-muted-foreground">{t("tablesAdmin.subtitle")}</p>
									</div>
									{pos.branchId && Number(pos.branchId) > 0 ? (
										<TablesManager branchId={Number(pos.branchId)} />
									) : (
										<p className="text-sm text-muted-foreground">
											{t("pos.form.selectBranchFirst")}
										</p>
									)}
								</div>
							)}

							{!pos.isMenuWeb && (
								<div className="space-y-3 rounded-lg border p-4">
									<div className="space-y-1">
										<Label className="text-base">{t("orderTypesAdmin.title")}</Label>
										<p className="text-sm text-muted-foreground">
											{t("orderTypesAdmin.branchWideHint")}
										</p>
									</div>
									{pos.branchId && Number(pos.branchId) > 0 ? (
										<OrderTypesManager branchId={Number(pos.branchId)} />
									) : (
										<p className="text-sm text-muted-foreground">
											{t("pos.form.selectBranchFirst")}
										</p>
									)}
								</div>
							)}

							{!pos.isMenuWeb && (
								<div className="space-y-3 rounded-lg border p-4">
									<div className="space-y-1">
										<Label className="text-base">{t("orderChannelsAdmin.title")}</Label>
										<p className="text-sm text-muted-foreground">
											{t("orderChannelsAdmin.branchWideHint")}
										</p>
									</div>
									{pos.branchId && Number(pos.branchId) > 0 ? (
										<OrderChannelsManager branchId={Number(pos.branchId)} />
									) : (
										<p className="text-sm text-muted-foreground">
											{t("pos.form.selectBranchFirst")}
										</p>
									)}
								</div>
							)}

							{!pos.isMenuWeb && (
								<div className="space-y-3 rounded-lg border p-4">
									<div className="space-y-1">
										<Label className="text-base">{t("orderChannelsAdmin.payments.title")}</Label>
										<p className="text-sm text-muted-foreground">
											{t("orderChannelsAdmin.payments.hint")}
										</p>
									</div>
									{pos.branchId && Number(pos.branchId) > 0 ? (
										<PaymentMethodBranchToggles branchId={Number(pos.branchId)} />
									) : (
										<p className="text-sm text-muted-foreground">
											{t("pos.form.selectBranchFirst")}
										</p>
									)}
								</div>
							)}

							{!pos.isMenuWeb && (
								<div className="space-y-3 rounded-lg border p-4">
									<div className="space-y-1">
										<Label className="text-base">{t("printerConfig.title")}</Label>
										<p className="text-sm text-muted-foreground">
											{t("printerConfig.branchWideHint")}
										</p>
									</div>
									{pos.branchId && Number(pos.branchId) > 0 ? (
										<PrinterConfigManager branchId={Number(pos.branchId)} />
									) : (
										<p className="text-sm text-muted-foreground">
											{t("pos.form.selectBranchFirst")}
										</p>
									)}
								</div>
							)}
						</CardContent>
					</Card>

					<div id="storefront-products" />
					<SectionHead num={4} title={t("pos.tabs.products")} hint={t("pos.form.productsDescription")} />
					<Card>
						<CardContent className="space-y-6 pt-6">
							<div className="space-y-4">
								<Label>{t("pos.form.products")}</Label>
								{!pos.isMenuWeb && (!pos.branchId || Number(pos.branchId) <= 0) ? (
									<p className="text-muted-foreground text-sm border rounded-lg p-4">
										{t("pos.form.selectBranchFirst")}
									</p>
								) : (
									<>
										<div className="space-y-2">
											<Input
												aria-label={t("pos.form.products")}
												placeholder={t("pos.form.searchProductsPlaceholder")}
												value={productSearchTerm}
												onChange={e => setProductSearchTerm(e.target.value)}
											/>
										</div>
										<div className="flex items-center py-2 gap-2 px-1">
											<Checkbox
												id="select-all-products"
												checked={
													allProductsSelected
														? true
														: someProductsSelected
															? "indeterminate"
															: false
												}
												onCheckedChange={checked =>
													handleSelectAllProducts(
														checked === true || checked === "indeterminate",
													)
												}
												className="w-6 h-6"
											/>
											<Label
												htmlFor="select-all-products"
												className="text-sm font-medium cursor-pointer"
											>
												{t("pos.form.selectAllProducts")}
											</Label>
										</div>
										<div className="border rounded-lg p-4 max-h-64 overflow-y-auto">
											{filteredProducts.map(product => (
												<div key={product.id} className="flex items-center py-2 gap-2">
													<Checkbox
														id={`product-${product.id}`}
														checked={selectedProductIds.includes(product.id)}
														onCheckedChange={() => handleProductToggle(product.id)}
														className="w-6 h-6"
													/>
													<Label
														htmlFor={`product-${product.id}`}
														className="text-sm font-normal cursor-pointer px-2"
													>
														{product.name} {product.barcode && `(${product.barcode})`}
													</Label>
												</div>
											))}
										</div>
									</>
								)}
							</div>
						</CardContent>
					</Card>

					{/* Sticky action bar */}
					<div className="sticky bottom-0 z-10 flex items-center justify-end gap-2 rounded-t-lg border-t bg-background/95 px-3 py-3 backdrop-blur">
						<Button type="button" variant="outline" onClick={() => navigate("/pos/list")} disabled={saving}>
							{t("common.cancel")}
						</Button>
						<Button
							onClick={handleSubmit}
							disabled={
								saving ||
								(storefrontEnabled &&
									(storefrontSettings.loading ||
										storefrontSettings.error ||
										!!storefrontSettings.pendingUploads))
							}
							className="w-full sm:w-40"
						>
							{saving ? (
								<div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mx-2"></div>
							) : (
								<Save className="h-4 w-4 mx-2" />
							)}
							{t("common.save")}
						</Button>
					</div>
				</div>
				{storefrontEnabled && (
					<aside className="sticky top-4 hidden min-w-0 xl:block">
						<StorefrontPreview
							model={storefrontSettings}
							products={products.filter(product => selectedProductIds.includes(product.id))}
							banners={storefrontBanners}
						/>
					</aside>
				)}
			</div>
			<Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
				<DialogContent className="max-h-[90dvh] overflow-auto sm:max-w-md">
					<DialogHeader>
						<DialogTitle>{t("storefrontDesign.preview")}</DialogTitle>
						<DialogDescription>{t("storefrontDesign.draftPreview")}</DialogDescription>
					</DialogHeader>
					<StorefrontPreview
						hideHeading
						model={storefrontSettings}
						products={products.filter(product => selectedProductIds.includes(product.id))}
						banners={storefrontBanners}
					/>
				</DialogContent>
			</Dialog>
		</div>
	);
};

export default PosDetailPage;
